import { KeyValueStore } from "../key-value/key-value-store";
import { Constants } from "../../globals/constants";
import { Nullable } from "../../globals/types";
import { isPresent } from "../../globals/functions";
import { MunicipalityStore } from "../municipality/municipality-store";
import moment from "moment";

export class ProjectDtoFactory {
    static makeProjectDto(formData: FormData, municipalityStore: MunicipalityStore, keyValueStore: KeyValueStore): CreationProjectDto | UsageProjectDto {
        const formDataObject: any = Object.fromEntries(
            Array.from(formData).filter(([_, v]) => v != null && v !== "")
        );
        const projectType = formDataObject.projectType;
        delete formDataObject.projectType;

        let geometrySourceDate = null;
        if (formDataObject.geometrySourceDate !== undefined) {
            geometrySourceDate = moment(formDataObject.geometrySourceDate, "DD.MM.YYYY").format("YYYY-MM-DD");
            delete formDataObject.geometrySourceDate;
        }

        switch (projectType) {
            case Constants.projectTypeConstructionPermit: {
                let enactmentDate = null;
                if (formDataObject.enactmentDate !== undefined) {
                    enactmentDate = moment(formDataObject.enactmentDate, "DD.MM.YYYY").format("YYYY-MM-DD");
                    delete formDataObject.enactmentDate;
                }

                let compensationDeadline = null;
                if (formDataObject.compensationDeadline !== undefined) {
                    compensationDeadline = moment(formDataObject.compensationDeadline, "DD.MM.YYYY").format("YYYY-MM-DD");
                    delete formDataObject.compensationDeadline;
                }

                const cmiNrRawi = formDataObject.cmiNrRawi;
                delete formDataObject.cmiNrRawi;

                const cmiDocuments: CmiDocumentDto[] = [];
                if (cmiNrRawi !== undefined) {
                    cmiDocuments.push({ id: -1, department: Constants.departmentRawi, number: cmiNrRawi, type: null });
                }

                return {
                    ...formDataObject,
                    category: Constants.projectCategoryUsage,
                    type: projectType,
                    enactmentDate: enactmentDate,
                    compensationDeadline: compensationDeadline,
                    contacts: [],
                    cmiDocuments: cmiDocuments,
                    geometrySourceDate: geometrySourceDate
                };
            }
            case Constants.projectTypeLandUsePlanChange: {
                let enactmentDate = null;
                if (formDataObject.enactmentDate !== undefined) {
                    enactmentDate = moment(formDataObject.enactmentDate, "DD.MM.YYYY").format("YYYY-MM-DD");
                    delete formDataObject.enactmentDate;
                }

                let compensationDeadline = null;
                if (formDataObject.compensationDeadline !== undefined) {
                    compensationDeadline = moment(formDataObject.compensationDeadline, "DD.MM.YYYY").format("YYYY-MM-DD");
                    delete formDataObject.compensationDeadline;
                }

                const cmiDocuments: CmiDocumentDto[] = [];
                const cmiNrUwePre = formDataObject.cmiNrUwePre?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrUwePre;
                const cmiNrRawiPre = formDataObject.cmiNrRawiPre?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrRawiPre;
                const cmiNrUweApproved = formDataObject.cmiNrUweApproved?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrUweApproved;
                const cmiNrRawiApproved = formDataObject.cmiNrRawiApproved?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrRawiApproved;

                cmiDocuments.push(...cmiNrUwePre.map(nr => {
                    return { department: Constants.departmentUwe, number: nr, type: "vorprüfung" };
                }));
                cmiDocuments.push(...cmiNrRawiPre.map(nr => {
                    return { department: Constants.departmentRawi, number: nr, type: "vorprüfung" };
                }));
                cmiDocuments.push(...cmiNrUweApproved.map(nr => {
                    return { department: Constants.departmentUwe, number: nr, type: "genehmigung" };
                }));
                cmiDocuments.push(...cmiNrRawiApproved.map(nr => {
                    return { department: Constants.departmentRawi, number: nr, type: "genehmigung" };
                }));

                let name = formDataObject.name;

                if (name === undefined) {
                    name = [
                        municipalityStore.getMunicipality(parseInt(formDataObject.municipality)).name,
                        formDataObject.place,
                        keyValueStore.getRevisionTypeDisplayText(formDataObject.revision)
                    ].filter(v => v !== undefined).join(" - ");
                }

                return {
                    ...formDataObject,
                    category: Constants.projectCategoryUsage,
                    type: projectType,
                    name: name,
                    enactmentDate: enactmentDate,
                    compensationDeadline: compensationDeadline,
                    contacts: [],
                    cmiDocuments: cmiDocuments,
                    geometrySourceDate: geometrySourceDate
                };
            }
            case Constants.projectTypeLandfillSite: {
                let constructionPermitDate = null;
                if (formDataObject.constructionPermitDate !== undefined) {
                    constructionPermitDate = moment(formDataObject.constructionPermitDate, "DD.MM.YYYY").format("YYYY-MM-DD");
                    delete formDataObject.constructionPermitDate;
                }

                const cmiDocuments: CmiDocumentDto[] = [];
                const cmiNrUwePre = formDataObject.cmiNrUwePre?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrUwePre;
                const cmiNrRawiPre = formDataObject.cmiNrRawiPre?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrRawiPre;
                const cmiNrUweApproved = formDataObject.cmiNrUweApproved?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrUweApproved;
                const cmiNrRawiApproved = formDataObject.cmiNrRawiApproved?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrRawiApproved;

                cmiDocuments.push(...cmiNrUwePre.map(nr => {
                    return { department: Constants.departmentUwe, number: nr, type: "vorprüfung" };
                }));
                cmiDocuments.push(...cmiNrRawiPre.map(nr => {
                    return { department: Constants.departmentRawi, number: nr, type: "vorprüfung" };
                }));
                cmiDocuments.push(...cmiNrUweApproved.map(nr => {
                    return { department: Constants.departmentUwe, number: nr, type: "genehmigung" };
                }));
                cmiDocuments.push(...cmiNrRawiApproved.map(nr => {
                    return { department: Constants.departmentRawi, number: nr, type: "genehmigung" };
                }));

                let name = formDataObject.name;

                if (name === undefined) {
                    name = [
                        keyValueStore.getLandfillSiteTypeDisplayText(formDataObject.landfillSiteType),
                        formDataObject.place
                    ].filter(v => v !== undefined).join(" - ");
                }

                let enactmentDate = null;
                if (formDataObject.enactmentDate !== undefined) {
                    enactmentDate = moment(formDataObject.enactmentDate, "DD.MM.YYYY").format("YYYY-MM-DD");
                    delete formDataObject.enactmentDate;
                }

                return {
                    ...formDataObject,
                    category: Constants.projectCategoryUsage,
                    type: projectType,
                    name: name,
                    enactmentDate: enactmentDate,
                    constructionPermitDate: constructionPermitDate,
                    contacts: [],
                    cmiDocuments: cmiDocuments,
                    geometrySourceDate: geometrySourceDate
                };
            }
            case Constants.projectTypeSoilImprovement: {
                let enactmentDate = null;
                if (formDataObject.enactmentDate !== undefined) {
                    enactmentDate = moment(formDataObject.enactmentDate, "DD.MM.YYYY").format("YYYY-MM-DD");
                    delete formDataObject.enactmentDate;
                }

                const contacts: ContactDto[] = [];

                if (formDataObject.landOwnerId !== undefined) {
                    contacts.push({ id: formDataObject.landOwnerId, type: Constants.contactTypeOwner });
                    delete formDataObject.landOwnerId;
                }

                if (formDataObject.constructionSupervisorId !== undefined) {
                    contacts.push({
                        id: formDataObject.constructionSupervisorId,
                        type: Constants.contactTypeConstructionSupervisor
                    });
                    delete formDataObject.constructionSupervisorId;
                }

                const cmiDocuments: CmiDocumentDto[] = [];
                const cmiNrRawi = formDataObject.cmiNrRawi;
                delete formDataObject.cmiNrRawi;

                if (cmiNrRawi !== undefined) {
                    cmiDocuments.push({ id: -1, department: Constants.departmentRawi, number: cmiNrRawi, type: null });
                }

                return {
                    ...formDataObject,
                    category: Constants.projectCategoryCreation,
                    type: projectType,
                    enactmentDate: enactmentDate,
                    contacts: contacts,
                    cmiDocuments: cmiDocuments,
                    geometrySourceDate: geometrySourceDate
                };
            }
            case Constants.projectTypeDezoning: {
                let enactmentDate = null;
                if (formDataObject.enactmentDate !== undefined) {
                    enactmentDate = moment(formDataObject.enactmentDate, "DD.MM.YYYY").format("YYYY-MM-DD");
                    delete formDataObject.enactmentDate;
                }

                const contacts: ContactDto[] = [];

                if (formDataObject.landOwnerId !== undefined) {
                    contacts.push({ id: formDataObject.landOwnerId, type: Constants.contactTypeOwner });
                    delete formDataObject.landOwnerId;
                }

                const cmiDocuments: CmiDocumentDto[] = [];
                const cmiNrUwePre = formDataObject.cmiNrUwePre?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrUwePre;
                const cmiNrRawiPre = formDataObject.cmiNrRawiPre?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrRawiPre;
                const cmiNrUweApproved = formDataObject.cmiNrUweApproved?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrUweApproved;
                const cmiNrRawiApproved = formDataObject.cmiNrRawiApproved?.split(",").map(e => e.trim()) ?? [];
                delete formDataObject.cmiNrRawiApproved;

                cmiDocuments.push(...cmiNrUwePre.map(nr => {
                    return { department: Constants.departmentUwe, number: nr, type: "vorprüfung" };
                }));
                cmiDocuments.push(...cmiNrRawiPre.map(nr => {
                    return { department: Constants.departmentRawi, number: nr, type: "vorprüfung" };
                }));
                cmiDocuments.push(...cmiNrUweApproved.map(nr => {
                    return { department: Constants.departmentUwe, number: nr, type: "genehmigung" };
                }));
                cmiDocuments.push(...cmiNrRawiApproved.map(nr => {
                    return { department: Constants.departmentRawi, number: nr, type: "genehmigung" };
                }));

                return {
                    ...formDataObject,
                    category: Constants.projectCategoryCreation,
                    type: projectType,
                    enactmentDate: enactmentDate,
                    contacts: contacts,
                    cmiDocuments: cmiDocuments,
                    geometrySourceDate: geometrySourceDate
                };
            }
            case Constants.projectTypeRecultivation: {
                delete formDataObject.enactmentDate;
                delete formDataObject.governmentCouncilDecisionNr;
                delete formDataObject.ebageNr;
                delete formDataObject.geometrySourceDate;

                let operatingPermitDate = null;
                if (formDataObject.operatingPermitDate !== undefined) {
                    operatingPermitDate = moment(formDataObject.operatingPermitDate, "DD.MM.YYYY").format("YYYY-MM-DD");
                    delete formDataObject.operatingPermitDate;
                }

                let maintenanceDirectiveDate = null;
                if (formDataObject.maintenanceDirectiveDate !== undefined) {
                    maintenanceDirectiveDate = moment(formDataObject.maintenanceDirectiveDate, "DD.MM.YYYY").format("YYYY-MM-DD");
                    delete formDataObject.maintenanceDirectiveDate;
                }

                const contacts: ContactDto[] = [];

                if (formDataObject.constructionSupervisorId !== undefined) {
                    contacts.push({
                        id: formDataObject.constructionSupervisorId,
                        type: Constants.contactTypeConstructionSupervisor
                    });
                    delete formDataObject.constructionSupervisorId;
                }

                let name = formDataObject.name;

                if (name === undefined) {
                    name = [
                        keyValueStore.getLandfillSiteTypeDisplayText(formDataObject.landfillSiteType),
                        formDataObject.place
                    ].filter(v => v !== undefined).join(" - ");
                }

                return {
                    ...formDataObject,
                    category: Constants.projectCategoryCreation,
                    type: projectType,
                    name: name,
                    status: "in_bearbeitung",
                    operatingPermitDate: operatingPermitDate,
                    maintenanceDirectiveDate: maintenanceDirectiveDate,
                    contacts: contacts,
                    cmiDocuments: []
                };
            }
            default:
                throw new Error("Unknown project type");
        }
    }
}

export class ProjectDtoValidator {
    static checkRequiredFields(projectDto: UsageProjectDto | CreationProjectDto): boolean {
        switch (projectDto.type) {
            case Constants.projectTypeConstructionPermit: {
                const usageProjectDto = projectDto as UsageProjectDto;
                return isPresent(usageProjectDto.name) &&
                    isPresent(usageProjectDto.municipality) &&
                    isPresent(usageProjectDto.place) &&
                    isPresent(usageProjectDto.usageCategory) &&
                    isPresent(usageProjectDto.status) &&
                    isPresent(usageProjectDto.ebageNr) &&
                    isPresent(usageProjectDto.targetArea)
            }

            case Constants.projectTypeLandUsePlanChange: {
                const usageProjectDto = projectDto as UsageProjectDto;
                return isPresent(usageProjectDto.name) &&
                    isPresent(usageProjectDto.municipality) &&
                    isPresent(usageProjectDto.revision) &&
                    isPresent(usageProjectDto.status) &&
                    isPresent(usageProjectDto.targetArea) &&
                    isPresent(usageProjectDto.cmiDocuments.find(e => e.department === Constants.departmentRawi && e.type === "vorprüfung" && isPresent(e.number)))
            }

            case Constants.projectTypeLandfillSite: {
                const usageProjectDto = projectDto as UsageProjectDto;
                return isPresent(usageProjectDto.name) &&
                    isPresent(usageProjectDto.municipality) &&
                    isPresent(usageProjectDto.place) &&
                    isPresent(usageProjectDto.landfillSiteType) &&
                    isPresent(usageProjectDto.status) &&
                    isPresent(usageProjectDto.cmiDocuments.find(e => e.department === Constants.departmentRawi && e.type === "vorprüfung" && isPresent(e.number)))
            }

            case Constants.projectTypeSoilImprovement: {
                const usageProjectDto = projectDto as UsageProjectDto;
                return isPresent(usageProjectDto.name) &&
                    isPresent(usageProjectDto.municipality) &&
                    isPresent(usageProjectDto.place) &&
                    isPresent(usageProjectDto.ebageNr) &&
                    isPresent(usageProjectDto.status)
            }

            case Constants.projectTypeDezoning: {
                const usageProjectDto = projectDto as UsageProjectDto;
                return isPresent(usageProjectDto.name) &&
                    isPresent(usageProjectDto.municipality) &&
                    isPresent(usageProjectDto.revision) &&
                    isPresent(usageProjectDto.status) &&
                    isPresent(usageProjectDto.cmiDocuments.find(e => e.department === Constants.departmentRawi && e.type === "vorprüfung" && isPresent(e.number)))
            }

            default:
                throw new Error("Unknown project type")
        }
    }
}

export type UsageProjectDto = {
    id: number;
    category: string;
    name: string;
    type: string;
    status: string;
    municipality: Nullable<number>;
    revision: Nullable<string>;
    usageCategory: Nullable<string>;
    landfillSiteType: Nullable<string>;
    compensationDeadline: Nullable<string>;
    place: Nullable<string>;
    governmentCouncilDecisionNr: Nullable<string>;
    enactmentDate: Nullable<string>;
    ebageNr: Nullable<string>;
    remarks: Nullable<string>;
    targetArea: Nullable<number>;
    constructionPermitDate: Nullable<string>;
    geometrySource: Nullable<string>;
    geometrySourceDate: Nullable<string>;
    contacts: ContactDto[];
    cmiDocuments: CmiDocumentDto[];
    ownerId: number;
    compensationApproved: boolean;
}

export type CreationProjectDto = {
    id: number;
    category: string;
    name: string;
    type: string;
    status: string;
    municipality: Nullable<number>;
    revision: Nullable<string>;
    place: Nullable<string>;
    governmentCouncilDecisionNr: Nullable<string>;
    enactmentDate: Nullable<string>;
    enactmentRemarks: Nullable<string>;
    ebageNr: Nullable<string>;
    operatingPermitDate: Nullable<string>;
    maintenanceDirectiveDate: Nullable<string>;
    creationGoalSource: Nullable<string>;
    approvedCreationArea: Nullable<number>;
    approvedRecultivationArea: Nullable<number>;
    requirementsSpecificationBBBStatus: Nullable<string>;
    requirementsSpecificationRequestSource: Nullable<string>;
    startConstructionMeetingStatus: Nullable<string>;
    startConstructionMeetingSource: Nullable<string>;
    creationGoalRemarks: Nullable<string>;
    generalRemarks: Nullable<string>;
    geometrySource: Nullable<string>;
    geometrySourceDate: Nullable<string>;
    contacts: ContactDto[];
    cmiDocuments: CmiDocumentDto[];
    ownerId: number;
}

export type CmiDocumentDto = {
    id: number;
    department: string;
    number: string;
    type: Nullable<string>;
}

type ContactDto = {
    id: number;
    type: string;
}
